import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Braces,
    CheckCircle2,
    FileCheck2,
    FilePlus,
    Hammer,
    Lock,
    Play,
    Save,
    ShieldAlert,
    Trash2,
} from "lucide-react";
import { parameterSets as paramSetsApi, runs as runsApi } from "@/api/endpoints";
import { serverDetail } from "@/api/client";

import Card from "@/components/Card";
import StatusPill from "@/components/StatusPill";
import SpinnerButton from "@/components/SpinnerButton";
import ErrorList from "@/components/ErrorList";
import ConfirmDialog from "@/components/ConfirmDialog";
import YamlViewer from "@/components/YamlViewer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const RUN_TYPES = ["monte_carlo", "sweep", "guidance_compare"];

export default function ParameterSetDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [runType, setRunType] = useState("monte_carlo");
    const [tagsText, setTagsText] = useState("");
    const [requestText, setRequestText] = useState("");
    const [requestJsonError, setRequestJsonError] = useState("");
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [validateRes, setValidateRes] = useState(null);
    const [compiling, setCompiling] = useState(false);
    const [compileRes, setCompileRes] = useState(null);
    const [confirmDel, setConfirmDel] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [runSubmitting, setRunSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setErr("");
            try {
                const d = await paramSetsApi.get(id);
                if (cancelled) return;
                setData(d);
                setName(d.name);
                setDescription(d.description || "");
                setRunType(d.run_type);
                setTagsText((d.tags || []).join(", "));
                setRequestText(JSON.stringify(d.client_request, null, 2));
            } catch (e) {
                if (!cancelled) setErr(serverDetail(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    function parseRequest() {
        setRequestJsonError("");
        try {
            const j = JSON.parse(requestText);
            return j;
        } catch (e) {
            setRequestJsonError(`JSON parse error: ${e.message}`);
            return null;
        }
    }

    function formatJson() {
        const j = parseRequest();
        if (!j) return;
        setRequestText(JSON.stringify(j, null, 2));
    }

    async function onSave() {
        if (!data || data.is_seed) return;
        const cr = parseRequest();
        if (!cr) return;
        setSaving(true);
        setErr("");
        try {
            const tags = tagsText
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
            const updated = await paramSetsApi.update(id, {
                name,
                description: description || null,
                run_type: runType,
                client_request: cr,
                tags,
            });
            setData(updated);
            setName(updated.name);
            setDescription(updated.description || "");
            setRunType(updated.run_type);
            setTagsText((updated.tags || []).join(", "));
            setRequestText(JSON.stringify(updated.client_request, null, 2));
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setSaving(false);
        }
    }

    async function onValidate() {
        setValidating(true);
        setValidateRes(null);
        try {
            const r = await paramSetsApi.validate(id);
            setValidateRes(r);
        } catch (e) {
            const data = e?.response?.data;
            setValidateRes(
                data?.errors
                    ? { ok: false, errors: data.errors }
                    : { ok: false, errors: [{ msg: serverDetail(e), type: "client_error", loc: [] }] },
            );
        } finally {
            setValidating(false);
        }
    }

    async function onCompile() {
        setCompiling(true);
        setCompileRes(null);
        try {
            const r = await paramSetsApi.compile(id);
            setCompileRes(r);
        } catch (e) {
            const data = e?.response?.data;
            setCompileRes(
                data?.errors
                    ? { ok: false, errors: data.errors }
                    : { ok: false, errors: [{ msg: serverDetail(e), type: "client_error", loc: [] }] },
            );
        } finally {
            setCompiling(false);
        }
    }

    async function doRun() {
        setRunSubmitting(true);
        setErr("");
        try {
            const r = await runsApi.submit({
                parameter_set_id: id,
                run_type: runType,
            });
            navigate(`/runs/${r.id}`);
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setRunSubmitting(false);
        }
    }

    async function doDelete() {
        setDeleting(true);
        try {
            await paramSetsApi.remove(id);
            navigate("/parameter-sets");
        } catch (e) {
            setErr(serverDetail(e));
            setDeleting(false);
            setConfirmDel(false);
        }
    }

    const lineCount = useMemo(
        () => Math.max(1, requestText.split("\n").length),
        [requestText],
    );
    const validatePass = validateRes?.ok;

    if (loading) {
        return (
            <div className="text-sm text-ux-text-3" data-testid="param-set-detail-loading">
                Loading parameter set…
            </div>
        );
    }
    if (err && !data) {
        return (
            <div
                className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger"
                data-testid="param-set-detail-error"
            >
                <ShieldAlert className="h-4 w-4 mt-0.5" />
                <span>{err}</span>
            </div>
        );
    }
    if (!data) return null;

    const readonly = data.is_seed;

    return (
        <div className="space-y-5" data-testid="param-set-detail-page">
            <div>
                <Link
                    to="/parameter-sets"
                    className="inline-flex items-center gap-1 text-xs text-ux-text-3 hover:text-ux-text-1 transition-colors"
                    data-testid="param-set-detail-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Parameter sets
                </Link>
                <div className="mt-2 flex flex-wrap items-end gap-3">
                    <div className="min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3">
                            Parameter set
                        </div>
                        <h1
                            className="text-2xl font-semibold text-ux-text-1 truncate flex items-center gap-2"
                            data-testid="param-set-detail-title"
                        >
                            {data.name}
                            {readonly && (
                                <Lock className="h-4 w-4 text-ux-warning" aria-hidden />
                            )}
                        </h1>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-mono text-ux-text-3">
                            <span>{data.id}</span>
                            {data.is_seed && (
                                <StatusPill status="ok" testId="param-set-detail-seed">
                                    seed (read-only)
                                </StatusPill>
                            )}
                        </div>
                    </div>
                    <div className="flex-1" />
                    <div className="flex flex-wrap items-center gap-2">
                        <SpinnerButton
                            variant="secondary"
                            loading={validating}
                            onClick={onValidate}
                            testId="param-set-detail-validate"
                        >
                            <FileCheck2 className="w-4 h-4" /> Validate
                        </SpinnerButton>
                        <SpinnerButton
                            variant="secondary"
                            loading={compiling}
                            onClick={onCompile}
                            testId="param-set-detail-compile"
                        >
                            <Hammer className="w-4 h-4" /> Compile
                        </SpinnerButton>
                        <SpinnerButton
                            loading={runSubmitting}
                            onClick={doRun}
                            testId="param-set-detail-run"
                        >
                            <Play className="w-4 h-4" /> Run
                        </SpinnerButton>
                        {!readonly && (
                            <SpinnerButton
                                variant="danger"
                                onClick={() => setConfirmDel(true)}
                                testId="param-set-detail-delete"
                            >
                                <Trash2 className="w-4 h-4" /> Delete
                            </SpinnerButton>
                        )}
                    </div>
                </div>
            </div>

            {err && (
                <div
                    className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger"
                    data-testid="param-set-detail-error-banner"
                >
                    <ShieldAlert className="h-4 w-4 mt-0.5" />
                    <span>{err}</span>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                {/* Metadata column ─────────────────────────────────────────── */}
                <Card title="Metadata" testId="param-set-detail-meta-card" className="xl:col-span-1">
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-ux-text-3">
                                Name
                            </Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={readonly}
                                data-testid="param-set-detail-name"
                                className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 disabled:opacity-60"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-ux-text-3">
                                Description
                            </Label>
                            <Textarea
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={readonly}
                                data-testid="param-set-detail-description"
                                className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 disabled:opacity-60"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-ux-text-3">
                                Run type
                            </Label>
                            <Select value={runType} onValueChange={setRunType} disabled={readonly}>
                                <SelectTrigger
                                    data-testid="param-set-detail-runtype"
                                    className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 disabled:opacity-60"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {RUN_TYPES.map((t) => (
                                        <SelectItem key={t} value={t}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[11px] uppercase tracking-wider text-ux-text-3">
                                Tags (comma-separated)
                            </Label>
                            <Input
                                value={tagsText}
                                onChange={(e) => setTagsText(e.target.value)}
                                disabled={readonly}
                                placeholder="seed, demo, monte_carlo"
                                data-testid="param-set-detail-tags"
                                className="bg-ux-bg-2 border-ux-border-1 text-ux-text-1 disabled:opacity-60"
                            />
                        </div>
                        <SpinnerButton
                            disabled={readonly}
                            loading={saving}
                            onClick={onSave}
                            testId="param-set-detail-save"
                            className="w-full"
                        >
                            <Save className="w-4 h-4" /> Save
                        </SpinnerButton>
                        {readonly && (
                            <p className="text-[11px] text-ux-warning flex items-center gap-1.5">
                                <Lock className="h-3 w-3" />
                                Seed parameter sets cannot be edited or deleted.
                            </p>
                        )}
                    </div>
                </Card>

                {/* JSON editor + results column ───────────────────────────── */}
                <Card
                    testId="param-set-detail-request-card"
                    className="xl:col-span-2"
                    title={
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-ux-text-1">
                                <Braces className="h-4 w-4 text-ux-accent" />
                                <span>Client request (JSON)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={formatJson}
                                    disabled={readonly}
                                    className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-ux-border-1 text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2 disabled:opacity-50 transition-colors"
                                    data-testid="param-set-detail-format-json"
                                >
                                    <FilePlus className="h-3 w-3 inline mr-1" /> Format
                                </button>
                                <SpinnerButton
                                    variant="secondary"
                                    loading={validating}
                                    onClick={onValidate}
                                    testId="param-set-detail-validate-inline"
                                    className="!h-7 !px-2 !text-[11px]"
                                >
                                    <FileCheck2 className="w-3 h-3" /> Validate
                                </SpinnerButton>
                            </div>
                        </div>
                    }
                >
                    <div className="space-y-3">
                        <div className="relative rounded-sm border border-ux-border-1 bg-ux-bg-2 overflow-hidden">
                            <div className="absolute top-0 left-0 bottom-0 w-10 bg-ux-bg-3 border-r border-ux-border-1 text-[11px] font-mono text-ux-text-3 text-right pr-2 pt-2 select-none">
                                {Array.from({ length: lineCount }, (_, i) => (
                                    <div key={i} className="leading-5">
                                        {i + 1}
                                    </div>
                                ))}
                            </div>
                            <Textarea
                                value={requestText}
                                onChange={(e) => setRequestText(e.target.value)}
                                disabled={readonly}
                                rows={20}
                                className="!pl-12 !bg-ux-bg-2 !border-0 !text-[13px] !leading-5 font-mono text-ux-text-1 disabled:opacity-60 focus-visible:ring-0 focus-visible:ring-offset-0 resize-y"
                                data-testid="param-set-detail-client-request"
                            />
                        </div>
                        {requestJsonError && (
                            <div
                                className="flex items-start gap-2 rounded-sm border border-ux-danger/40 bg-ux-danger-soft px-3 py-2 text-sm text-ux-danger"
                                data-testid="param-set-detail-json-error"
                            >
                                <ShieldAlert className="h-4 w-4 mt-0.5" />
                                <span>{requestJsonError}</span>
                            </div>
                        )}
                        {validateRes && (
                            <div data-testid="param-set-detail-validate-result">
                                {validatePass ? (
                                    <div className="flex items-center gap-2 rounded-sm border border-ux-success/40 bg-ux-success-soft px-3 py-2 text-sm text-ux-success">
                                        <CheckCircle2 className="h-4 w-4" />
                                        Validation passed.
                                    </div>
                                ) : (
                                    <ErrorList errors={validateRes.errors} title="Validation errors" />
                                )}
                            </div>
                        )}
                        {compileRes && (
                            <div
                                data-testid="param-set-detail-compile-result"
                                className="space-y-2"
                            >
                                {compileRes.ok ? (
                                    <>
                                        <div className="flex items-center gap-2 rounded-sm border border-ux-success/40 bg-ux-success-soft px-3 py-2 text-sm text-ux-success">
                                            <CheckCircle2 className="h-4 w-4" />
                                            Compiled scenario:{" "}
                                            <span className="font-mono">
                                                {compileRes.summary?.name}
                                            </span>
                                        </div>
                                        <YamlViewer
                                            value={compileRes.compiled_yaml}
                                            maxHeight="40vh"
                                        />
                                    </>
                                ) : (
                                    <ErrorList errors={compileRes.errors} title="Compile errors" />
                                )}
                            </div>
                        )}
                    </div>
                </Card>
            </div>

            <ConfirmDialog
                open={confirmDel}
                title="Delete parameter set?"
                message={`This will permanently delete "${data.name}".`}
                confirmLabel="Delete"
                loading={deleting}
                onCancel={() => setConfirmDel(false)}
                onConfirm={doDelete}
                testId="param-set-detail-delete-confirm"
            />
        </div>
    );
}
